import { ANALYTICS_BY_L1_L3, type AnalyticItem, type AnalyticsComboKey } from "./analyticsByL1L3";
import type { ToggleOption } from "../../../shared/ToggleChartGroup";
import type { PrimitiveType } from "api/types/charts";

// ---------------------------------------------------------------------------
// L4 → ToggleChartGroup options
//
// Derives the chart toggle tabs from the same L4 catalogue that drives
// `AnalyticsPreview` (`ANALYTICS_BY_L1_L3`, keyed by `l1_dimension|l3_context`).
// Keeping this here means the L4 map stays the single source of truth: adding
// or renaming a metric updates both the preview and the chart tabs.
// ---------------------------------------------------------------------------

/** Stable, URL-safe value derived from a metric title (e.g. "% Correct" → "correct"). */
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const PROMPT_HIERARCHY_DEPENDENT_TITLES = [
  "% Independent",
  "% Steps Independent",
  "Prompt Level Distribution",
  "Latency By Prompt Level",
] as const;

const NET_OCCURRENCE_ONLY_TITLES = ["% Of Opportunities", "Opportunity Frequency"] as const;

const NET_CI_OR_PH_ONLY_TITLES = [
  "% Correct",
  "% Independent",
  "Prompt Level Distribution",
  "Trials to Criterion",
] as const;

const excludeTitles = (items: AnalyticItem[], titles: readonly string[]): AnalyticItem[] =>
  items.filter((item) => !titles.includes(item.title));

/**
 * Builds the toggle options for a target's L4 derived metrics.
 *
 * Mirrors `AnalyticsPreview`: looks up the `l1_dimension|l3_context` combo in
 * `ANALYTICS_BY_L1_L3` and maps each metric to a `{ value, label }` toggle
 * option. Returns an empty array when L1/L3 are missing or the combo has no
 * configured analytics — callers should hide the toggle in that case.
 *
 * @param l1Dimension - The target's L1 behavioral dimension.
 * @param l3Context - The target's L3 instructional context.
 * @returns Toggle options for each derived metric (may be empty).
 */
export function getAnalyticsToggleOptions(
  l1Dimension: string | null | undefined,
  l3Context: string | null | undefined,
  options?: {
    primitiveType?: PrimitiveType | null;
    hasPromptHierarchy?: boolean;
    countPromptedAsCorrect?: boolean;
    behaviorSubject?: string | null;
    targetType?: string | null;
  }
): ToggleOption[] {
  const items = getAnalyticsItems(l1Dimension, l3Context, options);
  if (items.length === 0) return [];

  return items.map((item) => ({ value: slugify(item.title), label: item.title, id: item.id }));
}

/**
 * Returns the filtered analytics item list (title + description + icon) for the
 * given L1/L3 context and optional target-specific configuration.
 *
 * This is the single source for both:
 * - chart toggle tabs (`getAnalyticsToggleOptions`)
 * - target-builder "what you'll see" analytics preview
 */
export function getAnalyticsItems(
  l1Dimension: string | null | undefined,
  l3Context: string | null | undefined,
  options?: {
    primitiveType?: PrimitiveType | null;
    hasPromptHierarchy?: boolean;
    countPromptedAsCorrect?: boolean;
    behaviorSubject?: string | null;
    targetType?: string | null;
  }
): AnalyticItem[] {
  if (!l1Dimension || !l3Context) return [];

  let items = ANALYTICS_BY_L1_L3[`${l1Dimension}|${l3Context}` as AnalyticsComboKey] ?? [];
  const isBehaviorReduction = options?.targetType === "behavior-reduction";
  const primitiveTypeRaw = options?.primitiveType ?? null;
  const primitiveType: PrimitiveType | null =
    isBehaviorReduction && l1Dimension === "frequency" && l3Context === "discrete-trial"
      ? "counter_correct_incorrect"
      : isBehaviorReduction && l1Dimension === "frequency" && l3Context === "net"
        ? primitiveTypeRaw === "counter_occurrence"
          ? "counter_occurrence"
          : "counter_correct_incorrect"
        : isBehaviorReduction && l1Dimension === "latency" && l3Context === "net"
          ? "latency"
          : primitiveTypeRaw;
  const hasPromptHierarchy = isBehaviorReduction ? false : options?.hasPromptHierarchy ?? false;
  const countPromptedAsCorrect = options?.countPromptedAsCorrect ?? false;
  // The API returns behavior_subject as an uppercase enum ("CAREGIVER"), not the
  // lowercase BehaviorSubject literal ("caregiver") used elsewhere in the FE form state.
  const isCaregiver = options?.behaviorSubject?.toLowerCase() === "caregiver";

  // Caregiver goals never use prompt hierarchy — remove all prompt-hierarchy-dependent options.
  if (isCaregiver) {
    items = excludeTitles(items, PROMPT_HIERARCHY_DEPENDENT_TITLES);
  }

  if (isBehaviorReduction) {
    items = excludeTitles(items, PROMPT_HIERARCHY_DEPENDENT_TITLES);
  }

  // Frequency / Discrete Trial tab rules:
  // - No prompt hierarchy: show % Correct, hide prompt-dependent tabs.
  // - Prompt hierarchy + count prompted as correct: show % Correct.
  // - Prompt hierarchy + do not count prompted as correct: show % Independent.
  if (l1Dimension === "frequency" && l3Context === "discrete-trial") {
    if (!hasPromptHierarchy || primitiveType === "counter_correct_incorrect") {
      items = items.filter(
        (item) => item.title !== "% Independent" && item.title !== "Prompt Level Distribution"
      );
    } else if (countPromptedAsCorrect) {
      items = items.filter((item) => item.title !== "% Independent");
    } else {
      items = items.filter((item) => item.title !== "% Correct");
    }
  }

  // Frequency / Task Analysis: prompt-only tabs are hidden when prompt hierarchy is absent.
  if (l1Dimension === "frequency" && l3Context === "task-analysis" && !hasPromptHierarchy) {
    items = items.filter(
      (item) => item.title !== "% Steps Independent" && item.title !== "Prompt Level Distribution"
    );
  }

  // Frequency / NET count-mode rules (ANOW-6909): NET now supports three
  // count types — occurrence (default), Correct/Incorrect, and Prompt
  // Hierarchy. Occurrence mode keeps the raw-count metrics and hides the
  // correctness/prompt metrics entirely; C/I and PH modes hide the
  // occurrence-only metrics and reuse the same % Correct / % Independent /
  // Prompt Level Distribution rules as frequency+discrete-trial above.
  if (l1Dimension === "frequency" && l3Context === "net") {
    const isOccurrenceMode = !hasPromptHierarchy && primitiveType !== "counter_correct_incorrect";
    if (isOccurrenceMode) {
      items = excludeTitles(items, NET_CI_OR_PH_ONLY_TITLES);
    } else {
      items = excludeTitles(items, NET_OCCURRENCE_ONLY_TITLES);
      if (!hasPromptHierarchy || primitiveType === "counter_correct_incorrect") {
        items = items.filter(
          (item) => item.title !== "% Independent" && item.title !== "Prompt Level Distribution"
        );
      } else if (countPromptedAsCorrect) {
        items = items.filter((item) => item.title !== "% Independent");
      } else {
        items = items.filter((item) => item.title !== "% Correct");
      }
    }
  }

  // Latency / NET: Latency By Prompt Level only applies once the target's
  // NET count type is Prompt Hierarchy (ANOW-6909).
  if (l1Dimension === "latency" && l3Context === "net" && !hasPromptHierarchy) {
    items = items.filter((item) => item.title !== "Latency By Prompt Level");
  }

  // % Correct / % Steps Correct tabs only appear when count_prompted_as_correct is enabled,
  // but only for primitives where correctness depends on the flag — which requires a prompt
  // hierarchy to exist in the first place (CPAC has no meaning without one). Binary-scored
  // task-analysis targets (no prompt hierarchy) always keep "% Steps Correct": the backend
  // computes it unconditionally, and there's no CPAC checkbox for them to configure.
  // For counter_correct_incorrect the standard "% Correct" is always meaningful.
  const correctnessDependsOnFlag =
    primitiveType === "counter_prompt_hierarchy" ||
    (primitiveType === "task_analysis" && hasPromptHierarchy);
  if (correctnessDependsOnFlag && !countPromptedAsCorrect) {
    items = items.filter((item) => item.title !== "% Correct" && item.title !== "% Steps Correct");
  }

  // Latency / Discrete Trial swaps the second metric based on prompt hierarchy.
  if (l1Dimension === "latency" && l3Context === "discrete-trial" && primitiveType === "latency") {
    items = items.map((item) =>
      item.title === "Latency By Prompt Level"
        ? {
            ...item,
            id: "trend",
            title: "Latency Trend",
            description: "Change in response latency over time",
          }
        : item
    );
  }

  return items;
}
